/* femme Appstle product runtime V13 | 2026-11-02
   Flexible Appstle selling-plan runtime. Subscription remains the default
   purchase state and the exact selected Shopify/Appstle selling plan is
   submitted natively.
   V13 writes the selected subscription price before revealing the headline price,
   guards it against later theme rewrites, and resolves ambiguous Appstle groups
   as soon as their native widget appears rather than using long back-off waits.
*/
(() => {
  'use strict';

  const A = window.FemmeAppstleMigration;
  if (!A) return;

  const S = A.state;

  A.publish = (plan, variant) => {
    const root = S.root;
    if (!root || !variant) return;

    root.dataset.fscProvider = 'appstle';
    root.dataset.fscSelection = S.selectionType;
    root.dataset.fscSelectedPlanId =
      S.selectionType === 'subscription' && plan ? plan.id : '';
    root.dataset.fscSubscriptionPriceCents = plan?.price
      ? String(plan.price)
      : '';
    root.dataset.fscOneTimePriceCents = String(variant.price || 0);

    root.dispatchEvent(
      new CustomEvent('fsc:purchase-option-changed', {
        bubbles: true,
        detail: {
          productId: String(S.product?.id || ''),
          provider: 'appstle',
          selectionType: S.selectionType,
          sellingPlanId:
            S.selectionType === 'subscription' && plan ? plan.id : '',
          sellingPlanName: plan?.name || '',
          deliveryFrequency: plan ? A.planDisplayLabel(plan) : '',
          subscriptionPriceCents: Number(plan?.price || 0),
          oneTimePriceCents: Number(variant.price || 0)
        }
      })
    );
  };

  A.headlinePriceContainer = () => {
    if (!S.root) return null;
    return (
      S.root.querySelector(`#price-${CSS.escape(String(S.root.dataset.section || ''))}`) ||
      S.root.querySelector('[id^="price-"]')
    );
  };

  A.displayedHeadlinePriceNode = () => {
    const container = A.headlinePriceContainer();
    if (!container) return null;

    const selectors = [
      '.price__sale .price-item--sale',
      '.price__sale .price-item',
      '.price__regular .price-item--regular',
      '.price__regular .price-item',
      '.price-item--sale',
      '.price-item--regular',
      '.price-item',
      '.money'
    ];

    for (const selector of selectors) {
      const candidates = Array.from(container.querySelectorAll(selector));
      const visible = candidates.find((candidate) => {
        if (!(candidate instanceof HTMLElement)) return false;
        const styles = window.getComputedStyle(candidate);
        const booting = S.root?.classList?.contains('femme-price-booting');
        return (
          styles.display !== 'none' &&
          (booting || styles.visibility !== 'hidden') &&
          styles.opacity !== '0' &&
          candidate.getClientRects().length > 0 &&
          String(candidate.textContent || '').trim() !== ''
        );
      });
      if (visible) return visible;
    }

    return null;
  };

  A.updateHeadlinePrice = (plan, variant) => {
    if (!S.root || !variant) return;

    const cents =
      S.selectionType === 'subscription' && plan
        ? Number(plan.price || 0)
        : Number(variant.price || 0);
    const formatted = A.money(cents);

    S.root
      .querySelectorAll('[data-femme-headline-price]')
      .forEach((priceElement) => {
        if (String(priceElement.textContent || '').trim() !== formatted) {
          priceElement.textContent = formatted;
        }
      });

    const themePriceNode = A.displayedHeadlinePriceNode();
    if (
      themePriceNode &&
      String(themePriceNode.textContent || '').trim() !== formatted
    ) {
      themePriceNode.textContent = formatted;
    }

    S.root.classList.remove('femme-price-booting');
  };

  A.installHeadlinePriceGuard = () => {
    S.priceObserver?.disconnect?.();

    if (!S.root) return;

    const mutationTouchesHeadlinePrice = (mutation) => {
      const current = A.headlinePriceContainer();
      if (!current) return false;

      const target = mutation.target;
      if (target === current || (target instanceof Node && current.contains(target))) {
        return true;
      }

      if (mutation.type !== 'childList') return false;

      return Array.from(mutation.addedNodes || []).some((node) => {
        if (!(node instanceof Element)) return false;
        return (
          node === current ||
          node.contains(current) ||
          node.matches?.('[id^="price-"]') ||
          Boolean(node.querySelector?.('[id^="price-"]'))
        );
      });
    };

    S.priceObserver = new MutationObserver((mutations) => {
      if (!mutations.some(mutationTouchesHeadlinePrice)) return;
      A.schedule();
    });

    S.priceObserver.observe(S.root, {
      childList: true,
      subtree: true,
      characterData: true
    });
  };

  A.updateButton = (plan, variant) => {
    if (!S.config?.changeAtcText) return;

    const button = S.root?.querySelector(
      'button.product-form__submit[name="add"],button[name="add"],form[action*="/cart/add"] button[type="submit"]'
    );

    if (
      !button ||
      button.disabled ||
      button.getAttribute('aria-disabled') === 'true'
    ) {
      return;
    }

    const target = Array.from(
      button.querySelectorAll(
        '[data-button-text],.button__text,.btn__text,.product-form__submit-text,span'
      )
    ).find((el) => {
      return (
        String(el.textContent || '').trim() &&
        !/spinner|loading|icon/i.test(String(el.className || ''))
      );
    });

    if (!target) return;

    const desired =
      S.selectionType === 'subscription'
        ? A.subCta(plan, variant)
        : String(S.config?.oneTimeCta || 'Add to cart').trim();

    if (desired) target.textContent = desired;
  };

  A.render = () => {
    if (!S.ui) return;

    const variant = A.getVariant();
    const plans = A.getPlans(variant);
    const refs = S.ui._f;

    if (!variant || !plans.length) {
      S.ui.hidden = true;
      S.root.dataset.fscProvider = 'appstle';
      S.root.dataset.fscSelection = 'none';
      S.root.dataset.fscSelectedPlanId = '';
      S.root.dataset.fscSubscriptionPriceCents = '';
      S.root.dataset.fscOneTimePriceCents = String(variant?.price || '');
      S.root.classList.remove('femme-price-booting');
      return;
    }

    S.ui.hidden = false;

    const plan = A.getPlan(variant) || A.defaultPlan(plans);
    if (!plan) return;
    S.selectedPlanId = plan.id;

    A.applyPlanPresentation?.(plan, variant);
    if (S.config?.subscriptionLabel) {
      S.config.subscriptionLabel = String(S.config.subscriptionLabel).replace(
        /\[saving\]%/gi,
        '[saving]'
      );
    }

    const isSub = S.selectionType === 'subscription';
    const qty = A.quantity();

    refs.subCard.classList.toggle('is-selected', isSub);
    refs.onceCard.classList.toggle('is-selected', !isSub);
    refs.sub.button.setAttribute('aria-checked', isSub ? 'true' : 'false');
    refs.once.button.setAttribute('aria-checked', isSub ? 'false' : 'true');
    refs.sub.label.textContent = A.subLabel(plan, variant);
    refs.sub.price.textContent = A.money(plan.price * qty);
    refs.once.label.textContent = String(
      S.config?.oneTimeLabel || 'One-time purchase'
    );
    refs.once.price.textContent = A.money(variant.price * qty);

    A.renderPlanSelect(plans, plan);
    A.renderBenefits(plan, variant);
    A.syncForms();
    A.updateButton(plan, variant);
    A.updateHeadlinePrice(plan, variant);
    A.publish(plan, variant);
    A.positionUi();
  };

  A.schedule = () => {
    if (S.raf) cancelAnimationFrame(S.raf);

    S.raf = requestAnimationFrame(() => {
      S.raf = null;
      A.render();
    });
  };

  A.installSectionFixStyles = () => {
    if (document.getElementById('femme-appstle-section-fixes')) return;

    const style = document.createElement('style');
    style.id = 'femme-appstle-section-fixes';
    style.textContent = `
      .fsc-ui[data-femme-appstle-ui="true"] .fsc-select-wrap::after {
        visibility: hidden !important;
        opacity: 0 !important;
        pointer-events: none !important;
      }
    `;
    document.head.appendChild(style);
  };

  A.patchCartAddFetch = () => {
    if (A._cartAddFetchPatched || typeof window.fetch !== 'function') return;
    A._cartAddFetchPatched = true;

    const nativeFetch = window.fetch.bind(window);

    window.fetch = function femmeAppstleFetch(input, init) {
      try {
        const url =
          typeof input === 'string'
            ? input
            : input instanceof Request
              ? input.url
              : String(input?.url || '');
        const method = String(
          init?.method || (input instanceof Request ? input.method : 'GET') || 'GET'
        ).toUpperCase();

        if (
          !/\/cart\/add(?:\.js)?(?:\?|$)/.test(url) ||
          method !== 'POST' ||
          !S.root?.isConnected
        ) {
          return nativeFetch(input, init);
        }

        const variantId = String(A.getVariantId?.() || '');
        if (!variantId) return nativeFetch(input, init);

        const planId = String(A.activePlanId?.() || '');
        const rawBody = init?.body;

        if (typeof FormData !== 'undefined' && rawBody instanceof FormData) {
          const submittedVariantId = String(rawBody.get('id') || '');

          if (submittedVariantId !== variantId) {
            return nativeFetch(input, init);
          }

          if (planId) {
            rawBody.set('selling_plan', planId);
          } else {
            rawBody.delete('selling_plan');
          }

          return nativeFetch(input, {
            ...(init || {}),
            body: rawBody
          });
        }

        if (typeof rawBody !== 'string') return nativeFetch(input, init);

        let contentType = '';
        if (init?.headers instanceof Headers) {
          contentType = init.headers.get('content-type') || '';
        } else if (Array.isArray(init?.headers)) {
          contentType = new Headers(init.headers).get('content-type') || '';
        } else if (init?.headers && typeof init.headers === 'object') {
          contentType =
            init.headers['Content-Type'] || init.headers['content-type'] || '';
        }

        if (!String(contentType).toLowerCase().includes('application/json')) {
          return nativeFetch(input, init);
        }

        const payload = JSON.parse(rawBody);
        let matchedPrimaryLine = false;

        const applyPurchaseState = (item) => {
          if (
            matchedPrimaryLine ||
            !item ||
            String(item.id || '') !== variantId
          ) {
            return;
          }

          matchedPrimaryLine = true;
          if (planId) {
            item.selling_plan = Number(planId) || planId;
          } else {
            delete item.selling_plan;
            delete item.sellingPlan;
            delete item.sellingPlanId;
          }
        };

        if (Array.isArray(payload?.items)) {
          payload.items.forEach(applyPurchaseState);
        } else {
          applyPurchaseState(payload);
        }

        if (!matchedPrimaryLine) return nativeFetch(input, init);

        return nativeFetch(input, {
          ...(init || {}),
          body: JSON.stringify(payload)
        });
      } catch (error) {
        console.error('[femme Appstle] Cart payload guard skipped.', error);
        return nativeFetch(input, init);
      }
    };
  };

  const rootFor = (productId = '') => {
    if (productId) {
      const exact = document.querySelector(
        `product-info[data-product-id="${CSS.escape(String(productId))}"]`
      );
      if (exact) return exact;
    }

    return (
      document.querySelector('product-info[id^="MainProduct-"]') ||
      document.querySelector('product-info[data-product-id]')
    );
  };

  const bind = () => {
    document.addEventListener('variant:changed', A.schedule);

    document.addEventListener('input', (event) => {
      if (
        S.root.contains(event.target) &&
        event.target.matches?.('input[name="quantity"]')
      ) {
        A.schedule();
      }
    });

    document.addEventListener('change', (event) => {
      if (
        S.root.contains(event.target) &&
        event.target.matches?.(
          'input[name="id"],select[name="id"],input[name="quantity"]'
        )
      ) {
        setTimeout(A.schedule, 0);
        setTimeout(A.schedule, 120);
      }
    });

    document.addEventListener('click', (event) => {
      if (
        S.root.contains(event.target) &&
        event.target.closest?.(
          'quantity-input,quantity-selector,.quantity,.quantity-selector'
        )
      ) {
        setTimeout(A.schedule, 0);
        setTimeout(A.schedule, 80);
      }
    });

    document.addEventListener(
      'submit',
      (event) => {
        if (
          event.target instanceof HTMLFormElement &&
          S.root.contains(event.target) &&
          String(event.target.action || '').includes('/cart/add')
        ) {
          A.syncForms();
        }
      },
      true
    );
  };

  const hasPlans = (variants) =>
    Array.from(variants?.values?.() || []).some(
      (variant) => Array.isArray(variant?.plans) && variant.plans.length
    );

  const resolveVariants = async (product) => {
    let variants = A.appstleData(product);
    if (hasPlans(variants) || !A.hasAmbiguousAppstleGroups?.(product)) {
      return variants;
    }

    return new Promise((resolve) => {
      let settled = false;
      let timer = null;
      let observer = null;
      const startedAt = Date.now();
      const timeoutMs = 1000;
      const pollMs = 40;

      const finish = () => {
        if (settled) return;
        settled = true;
        if (timer) window.clearTimeout(timer);
        observer?.disconnect?.();
        resolve(variants);
      };

      const check = () => {
        if (settled) return;
        if (timer) {
          window.clearTimeout(timer);
          timer = null;
        }

        variants = A.appstleData(product);
        if (
          hasPlans(variants) ||
          !A.hasAmbiguousAppstleGroups?.(product) ||
          Date.now() - startedAt >= timeoutMs
        ) {
          finish();
          return;
        }

        timer = window.setTimeout(check, pollMs);
      };

      if (typeof MutationObserver === 'function') {
        observer = new MutationObserver(() => check());
        observer.observe(S.root || document.documentElement, {
          childList: true,
          subtree: true,
          attributes: true
        });
      }

      timer = window.setTimeout(check, pollMs);
    });
  };

  const init = async () => {
    const controller = Array.from(
      document.querySelectorAll('[id^="FemmeSubscriptionCustomiser-"]')
    ).find((el) => el.querySelector('[data-fsc-config]'));

    if (!controller) return;

    const config = A.parseJson(controller.querySelector('[data-fsc-config]'));
    if (!config?.enabled) return;

    const root = rootFor(controller.dataset.fscProductId || '');
    if (!root) return;

    controller.dataset.fscProvider = 'appstle';
    root.dataset.fscProvider = 'appstle';

    S.controller = controller;
    S.root = root;
    S.config = config;
    S.baseConfig = JSON.parse(JSON.stringify(config));

    try {
      const product = await A.fetchProduct(root.dataset.url || location.pathname);
      S.product = product;

      const variants = await resolveVariants(product);

      if (!hasPlans(variants)) {
        const appstleGroupCount = A.appstleGroups?.(product)?.length || 0;
        if (appstleGroupCount > 1) {
          console.error(
            '[femme Appstle] Multiple Appstle plan groups are assigned but no single active group could be resolved.'
          );
        } else {
          console.error(
            '[femme Appstle] No active Appstle selling plan is assigned to this product.'
          );
        }
        root.classList.remove('femme-price-booting');
        return;
      }

      const legacy =
        window.__femmeSubscriptionCustomisers?.[
          String(controller.dataset.fscProductId || product.id || '')
        ];

      if (legacy?.destroy) legacy.destroy();

      root
        .querySelectorAll('.fsc-ui:not([data-femme-appstle-ui="true"])')
        .forEach((el) => el.remove());

      S.variants = variants;

      const current = A.getVariant();
      const plans = A.getPlans(current);
      if (!plans.length) {
        root.classList.remove('femme-price-booting');
        return;
      }

      const urlPlan = new URL(location.href).searchParams.get('selling_plan');
      const urlValid =
        config.respectUrl &&
        urlPlan &&
        plans.some((plan) => plan.id === String(urlPlan));

      S.selectionType = 'subscription';

      const defaultPlan = A.defaultPlan(plans);
      S.selectedPlanId = urlValid
        ? String(urlPlan)
        : String(defaultPlan?.id || '');

      A.installSectionFixStyles();
      A.patchCartAddFetch();
      A.buildUi();
      A.bindPreAddSync?.();
      A.installHeadlinePriceGuard();
      A.render();
      bind();
    } catch (error) {
      root.classList.remove('femme-price-booting');
      console.error('[femme Appstle] Product bridge failed.', error);
    }
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }
})();
